/**
 * OnboardingPage — Öffentlicher Onboarding-Assistent (/onboarding/:token).
 * Der Ablauf (Schritte & Felder) wird vom Studio im Onboarding-Baukasten
 * (Einstellungen) frei zusammengestellt und hier dynamisch gerendert.
 * System-Schritte: Stammdaten, Angebote, Einverständnis. Eigene Schritte:
 * frei definierte Felder, deren Antworten gespeichert werden.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, User, FileSignature, PartyPopper, ChevronRight, ChevronDown, ChevronUp, Dumbbell, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ConsentSignSection from '@/components/portal/ConsentSignSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.message || '').filter(Boolean).join(', ') || fallback;
  return fallback;
};

const STEP_ICON = { profile: User, offers: Dumbbell, consents: FileSignature, custom: ListChecks };

const SignaturePad = ({ value, onChange }) => {
  const ref = React.useRef(null);
  const drawing = React.useRef(false);
  const posOf = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; const c = ref.current.getContext('2d'); const p = posOf(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext('2d'); const p = posOf(e); c.lineWidth = 2; c.lineCap = 'round'; c.strokeStyle = '#ffffff'; c.lineTo(p.x, p.y); c.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL('image/png')); };
  const clear = () => { const c = ref.current.getContext('2d'); c.clearRect(0, 0, ref.current.width, ref.current.height); onChange(''); };
  return (
    <div className="mt-1">
      <canvas ref={ref} width={320} height={120} className="w-full rounded-md bg-white/5 border border-white/15 touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="onboarding-signature-pad" />
      <div className="flex items-center justify-between mt-1">
        <span className="text-white/40 text-xs">{value ? '✓ Unterschrift erfasst' : 'Hier unterschreiben'}</span>
        <button type="button" onClick={clear} className="text-white/60 text-xs underline">Löschen</button>
      </div>
    </div>
  );
};

const DEFAULT_STEPS = [
  { id: 'profile', type: 'profile', title: 'Stammdaten', enabled: true, fields: [] },
  { id: 'offers', type: 'offers', title: 'Angebote', enabled: true, fields: [] },
  { id: 'consents', type: 'consents', title: 'Einverständnis', enabled: true, fields: [] },
];

const OnboardingPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);

  const [profile, setProfile] = useState({});
  const [offers, setOffers] = useState([]);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [consentData, setConsentData] = useState(null);
  const [openFormId, setOpenFormId] = useState(null);
  const [consentPayload, setConsentPayload] = useState(null);
  const [responses, setResponses] = useState({});

  const stepList = useMemo(() => {
    const cfg = (data?.builder_steps && data.builder_steps.length) ? data.builder_steps : DEFAULT_STEPS;
    return cfg.filter((s) => s.enabled !== false);
  }, [data]);

  const loadConsents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/consent-sign/${token}`);
      setConsentData(res.data);
      const firstUnsigned = (res.data.forms || []).find(f => !f.signed);
      setOpenFormId(firstUnsigned?.id || null);
    } catch { /* ignore */ }
  }, [token]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/onboarding/${token}`);
      if (res.data.completed) { setCompleted(true); return; }
      setData(res.data);
      const p = res.data.profile || {};
      setProfile({
        first_name: p.first_name || '', last_name: p.last_name || '',
        email: p.email || '', email_pending: p.email_pending,
        gender: p.gender || '',
        date_of_birth: p.date_of_birth || '', street: p.street || '',
        postal_code: p.postal_code || '', city: p.city || '',
        emergency_name: p.emergency_contact?.name || '',
        emergency_phone: p.emergency_contact?.phone || '',
      });
      setSelectedOffers(res.data.selected_service_ids || []);
      setResponses(res.data.onboarding_responses || {});
      loadConsents();
      try {
        const params = res.data.studio_id ? { studio_id: res.data.studio_id } : {};
        const sv = await axios.get(`${API}/public/services`, { params });
        setOffers((sv.data || []).filter(s => s.is_active !== false));
      } catch { /* ignore */ }
    } catch (e) {
      setError(e.response?.status === 410
        ? 'Dieser Link ist abgelaufen. Bitte fordere einen neuen an.'
        : 'Dieser Link ist ungültig oder abgelaufen. Bitte kontaktiere uns.');
    }
  }, [token, loadConsents]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data && step === null && stepList.length) setStep(stepList[0].id); }, [data, step, stepList]);

  const activeStepIdx = stepList.findIndex(s => s.id === step);
  const current = stepList.find(s => s.id === step);
  const isLast = activeStepIdx === stepList.length - 1;

  const goNext = () => {
    if (activeStepIdx < stepList.length - 1) { setStep(stepList[activeStepIdx + 1].id); window.scrollTo(0, 0); }
    else finish();
  };
  const goPrev = () => {
    if (activeStepIdx > 0) { setStep(stepList[activeStepIdx - 1].id); window.scrollTo(0, 0); }
  };

  const saveProfile = async () => {
    if (!profile.first_name?.trim() || !profile.last_name?.trim()) { toast.error('Bitte Vor- und Nachname angeben'); return; }
    if (profile.email_pending && !profile.email) { toast.error('Bitte E-Mail angeben'); return; }
    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) { toast.error('Bitte eine gültige E-Mail angeben'); return; }
    if (!profile.gender) { toast.error('Bitte Geschlecht wählen'); return; }
    if (!profile.date_of_birth) { toast.error('Bitte Geburtsdatum angeben'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/public/onboarding/${token}/profile`, {
        first_name: profile.first_name.trim(),
        last_name: profile.last_name.trim(),
        email: profile.email_pending && profile.email ? profile.email : undefined,
        gender: profile.gender,
        date_of_birth: profile.date_of_birth,
        street: profile.street || undefined,
        postal_code: profile.postal_code || undefined,
        city: profile.city || undefined,
        emergency_contact: (profile.emergency_name || profile.emergency_phone)
          ? { name: profile.emergency_name, phone: profile.emergency_phone } : undefined,
      });
      goNext();
    } catch (e) {
      toast.error(errMsg(e, 'Speichern fehlgeschlagen'));
    } finally { setSaving(false); }
  };

  const toggleOffer = (id) => setSelectedOffers(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const saveOffers = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/public/onboarding/${token}/offers`, { service_ids: selectedOffers });
      await loadConsents();
      goNext();
    } catch (e) {
      toast.error(errMsg(e, 'Speichern fehlgeschlagen'));
    } finally { setSaving(false); }
  };

  const submitConsent = async (formId) => {
    if (!consentPayload || consentPayload.form_id !== formId) {
      toast.error('Bitte Checkliste, Erklärung und Unterschrift ausfüllen'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/public/consent-sign/${token}`, consentPayload);
      toast.success('Unterschrieben ✅');
      setConsentPayload(null);
      await loadConsents();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const saveCustom = async (stepCfg) => {
    for (const f of (stepCfg.fields || [])) {
      if (f.type === 'heading' || f.type === 'info' || !f.required) continue;
      if (!isVisible(f)) continue;
      const v = responses[f.key];
      if (v === undefined || v === '' || (Array.isArray(v) && !v.length)) { toast.error(`Bitte "${f.label}" ausfüllen`); return; }
    }
    setSaving(true);
    try {
      await axios.post(`${API}/public/onboarding/${token}/custom-responses`, { responses });
      goNext();
    } catch (e) {
      toast.error(errMsg(e, 'Speichern fehlgeschlagen'));
    } finally { setSaving(false); }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/public/onboarding/${token}/complete`);
      setCompleted(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(errMsg(e, 'Abschluss fehlgeschlagen'));
    } finally { setSaving(false); }
  };

  const inputCls = 'bg-white/5 border-white/15 text-white';
  const setResp = (key, v) => setResponses(prev => ({ ...prev, [key]: v }));

  const isVisible = (f) => {
    const c = f.visible_if;
    if (!c || !c.field) return true;
    const actual = responses[c.field];
    if (c.equals === undefined || c.equals === null || c.equals === '') return !(actual === undefined || actual === '' || actual === false || (Array.isArray(actual) && !actual.length));
    return String(actual) === String(c.equals);
  };

  const uploadFile = async (f, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/public/onboarding/${token}/upload?key=${encodeURIComponent(f.key)}`, fd);
      setResp(f.key, r.data.url);
      toast.success('Datei hochgeladen');
    } catch (e) { toast.error(errMsg(e, 'Upload fehlgeschlagen')); }
  };

  const renderCustomField = (f) => {
    const v = responses[f.key];
    if (f.type === 'heading') return <p className="text-white/80 font-semibold pt-2">{f.label}</p>;
    if (f.type === 'info') return <p className="text-white/60 text-sm">{f.label}</p>;
    const common = { className: inputCls, 'data-testid': `onboarding-field-${f.key}` };
    switch (f.type) {
      case 'textarea':
        return <textarea {...common} className={`${inputCls} w-full min-h-[80px] rounded-md px-3 py-2 text-sm`} value={v || ''} onChange={(e) => setResp(f.key, e.target.value)} placeholder={f.placeholder} />;
      case 'number':
        return <Input {...common} type="number" value={v ?? ''} onChange={(e) => setResp(f.key, e.target.value)} placeholder={f.placeholder} />;
      case 'date':
        return <Input {...common} type="date" value={v || ''} onChange={(e) => setResp(f.key, e.target.value)} />;
      case 'email':
        return <Input {...common} type="email" value={v || ''} onChange={(e) => setResp(f.key, e.target.value)} placeholder={f.placeholder} />;
      case 'phone':
        return <Input {...common} value={v || ''} onChange={(e) => setResp(f.key, e.target.value)} placeholder={f.placeholder} />;
      case 'boolean':
        return (
          <div className="flex gap-2 mt-1">
            {[['true', 'Ja'], ['false', 'Nein']].map(([val, lbl]) => (
              <button key={val} type="button" onClick={() => setResp(f.key, val === 'true')}
                data-testid={`onboarding-field-${f.key}-${val}`}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border ${String(v) === val ? 'bg-yellow-500 text-black border-yellow-500' : 'bg-white/5 text-white border-white/15'}`}>{lbl}</button>
            ))}
          </div>
        );
      case 'select':
        return (
          <div className="flex flex-wrap gap-2 mt-1" data-testid={`onboarding-field-${f.key}`}>
            {(f.options || []).map((o) => (
              <button key={o} type="button" onClick={() => setResp(f.key, o)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${v === o ? 'bg-yellow-500 text-black border-yellow-500' : 'bg-white/5 text-white border-white/15'}`}>{o}</button>
            ))}
          </div>
        );
      case 'multiselect':
        return (
          <div className="flex flex-wrap gap-2 mt-1" data-testid={`onboarding-field-${f.key}`}>
            {(f.options || []).map((o) => {
              const arr = Array.isArray(v) ? v : [];
              const sel = arr.includes(o);
              return (
                <button key={o} type="button" onClick={() => setResp(f.key, sel ? arr.filter(x => x !== o) : [...arr, o])}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${sel ? 'bg-yellow-500 text-black border-yellow-500' : 'bg-white/5 text-white border-white/15'}`}>{o}</button>
              );
            })}
          </div>
        );
      case 'upload':
        return (
          <div className="mt-1" data-testid={`onboarding-field-${f.key}`}>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => uploadFile(f, e.target.files?.[0])} className="text-white/70 text-sm" data-testid={`onboarding-upload-${f.key}`} />
            {v && <p className="text-green-400 text-xs mt-1">✓ Datei hochgeladen</p>}
          </div>
        );
      case 'signature':
        return <SignaturePad value={v} onChange={(d) => setResp(f.key, d)} />;
      default:
        return <Input {...common} value={v || ''} onChange={(e) => setResp(f.key, e.target.value)} placeholder={f.placeholder} />;
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-red-300 text-center max-w-md" data-testid="onboarding-error">
          <AlertTriangle className="w-8 h-8 mx-auto mb-3" />{error}
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="onboarding-done">
          <PartyPopper className="w-14 h-14 text-yellow-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Alles erledigt! 🎉</h1>
          <p className="text-white/60">Vielen Dank — deine Daten sind vollständig. Wir freuen uns auf dein Training!</p>
        </div>
      </div>
    );
  }

  if (!data || !current) return <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">Wird geladen…</div>;

  const forms = consentData?.forms || [];
  const allSigned = forms.length === 0 || forms.every(f => f.signed);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white py-8 px-4" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Willkommen 👋</h1>
          <p className="text-white/60 text-sm mt-1">Hoi {profile.first_name || data.profile?.first_name || ''} — bitte vervollständige deine Daten.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {stepList.map((s, i) => {
            const Icon = STEP_ICON[s.type] || ListChecks;
            const done = i < activeStepIdx;
            const active = i === activeStepIdx;
            return (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${active ? 'bg-yellow-500 text-black' : done ? 'bg-green-500/20 text-green-300' : 'bg-white/5 text-white/50'}`}>
                  {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                  {s.title}
                </div>
                {i < stepList.length - 1 && <ChevronRight className="w-3 h-3 text-white/30" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* STEP: Stammdaten */}
        {current.type === 'profile' && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
            {current.description && <p className="text-white/60 text-sm">{current.description}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Vorname *</Label>
                <Input className={inputCls} value={profile.first_name} onChange={e => setProfile({ ...profile, first_name: e.target.value })} data-testid="onboarding-firstname" /></div>
              <div><Label className="text-white/80">Nachname *</Label>
                <Input className={inputCls} value={profile.last_name} onChange={e => setProfile({ ...profile, last_name: e.target.value })} data-testid="onboarding-lastname" /></div>
            </div>
            {profile.email_pending && (
              <div><Label className="text-white/80">E-Mail *</Label>
                <Input type="email" className={inputCls} value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} data-testid="onboarding-email" /></div>
            )}
            <div>
              <Label className="text-white/80">Geschlecht *</Label>
              <div className="flex gap-2 mt-1">
                {[['male', 'Mann'], ['female', 'Frau']].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => setProfile({ ...profile, gender: v })}
                    data-testid={`onboarding-gender-${v}`}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border ${profile.gender === v ? 'bg-yellow-500 text-black border-yellow-500' : 'bg-white/5 text-white border-white/15'}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div><Label className="text-white/80">Geburtsdatum *</Label>
              <Input type="date" className={inputCls} value={profile.date_of_birth} onChange={e => setProfile({ ...profile, date_of_birth: e.target.value })} data-testid="onboarding-dob" /></div>
            <div><Label className="text-white/80">Strasse & Nr.</Label>
              <Input className={inputCls} value={profile.street} onChange={e => setProfile({ ...profile, street: e.target.value })} data-testid="onboarding-street" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">PLZ</Label>
                <Input className={inputCls} value={profile.postal_code} onChange={e => setProfile({ ...profile, postal_code: e.target.value })} data-testid="onboarding-plz" /></div>
              <div><Label className="text-white/80">Ort</Label>
                <Input className={inputCls} value={profile.city} onChange={e => setProfile({ ...profile, city: e.target.value })} data-testid="onboarding-city" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Notfallkontakt Name</Label>
                <Input className={inputCls} value={profile.emergency_name} onChange={e => setProfile({ ...profile, emergency_name: e.target.value })} data-testid="onboarding-emg-name" /></div>
              <div><Label className="text-white/80">Notfallkontakt Tel.</Label>
                <Input className={inputCls} value={profile.emergency_phone} onChange={e => setProfile({ ...profile, emergency_phone: e.target.value })} data-testid="onboarding-emg-phone" /></div>
            </div>
            <div className="flex gap-2">
              {activeStepIdx > 0 && <Button variant="outline" onClick={goPrev} className="border-white/20 text-white bg-transparent hover:bg-white/10">Zurück</Button>}
              <Button onClick={saveProfile} disabled={saving} className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-bold" data-testid="onboarding-profile-next">
                {saving ? 'Speichert…' : (isLast ? 'Abschliessen' : 'Weiter')} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP: Angebote (Probe) */}
        {current.type === 'offers' && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4" data-testid="onboarding-offers-step">
            <p className="text-white/60 text-sm">{current.description || 'Was möchtest du ausprobieren? Wähle ein oder mehrere Angebote.'}</p>
            {offers.length === 0 && (
              <p className="text-white/40 text-sm text-center py-4">Zurzeit sind keine Probe-Angebote hinterlegt. Du kannst direkt weiter.</p>
            )}
            <div className="space-y-2">
              {offers.map(o => {
                const sel = selectedOffers.includes(o.id);
                return (
                  <button key={o.id} type="button" onClick={() => toggleOffer(o.id)}
                    data-testid={`onboarding-offer-${o.id}`}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left border transition-colors ${sel ? 'bg-yellow-500/15 border-yellow-500' : 'bg-white/5 border-white/15 hover:bg-white/10'}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{o.name}</p>
                      {(o.duration_minutes || o.duration) && <p className="text-xs text-white/50">{o.duration_minutes || o.duration} Min.</p>}
                    </div>
                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${sel ? 'bg-yellow-500 border-yellow-500' : 'border-white/30'}`}>
                      {sel && <CheckCircle2 className="w-4 h-4 text-black" />}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              {activeStepIdx > 0 && <Button variant="outline" onClick={goPrev} className="border-white/20 text-white bg-transparent hover:bg-white/10">Zurück</Button>}
              <Button onClick={saveOffers} disabled={saving} className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-bold" data-testid="onboarding-offers-next">
                {saving ? 'Speichert…' : (isLast ? 'Abschliessen' : 'Weiter')} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP: Einverständnisse */}
        {current.type === 'consents' && (
          <div className="space-y-4">
            <p className="text-white/60 text-sm text-center">{current.description || 'Bitte lies und unterschreibe die Erklärungen.'}</p>
            {forms.map(form => (
              <div key={form.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`onboarding-consent-${form.id}`}>
                <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-left"
                  onClick={() => !form.signed && setOpenFormId(openFormId === form.id ? null : form.id)}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{form.title}</p>
                    {form.signed
                      ? <p className="text-xs text-green-400 flex items-center gap-1 mt-0.5"><CheckCircle2 className="w-3 h-3" /> Unterschrieben</p>
                      : <p className="text-xs text-amber-400 flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3 h-3" /> Ausstehend</p>}
                  </div>
                  {form.signed ? <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                    : (openFormId === form.id ? <ChevronUp className="w-4 h-4 text-white/50 shrink-0" /> : <ChevronDown className="w-4 h-4 text-white/50 shrink-0" />)}
                </button>
                {!form.signed && openFormId === form.id && (
                  <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
                    <ConsentSignSection form={form} onChange={setConsentPayload} />
                    <Button onClick={() => submitConsent(form.id)} disabled={saving} className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold" data-testid={`onboarding-consent-submit-${form.id}`}>
                      {saving ? 'Speichert…' : '✍️ Verbindlich unterschreiben'}
                    </Button>
                  </div>
                )}
              </div>
            ))}
            <div className="flex gap-2">
              {activeStepIdx > 0 && <Button variant="outline" onClick={goPrev} className="border-white/20 text-white bg-transparent hover:bg-white/10">Zurück</Button>}
              <Button onClick={goNext} disabled={saving || !allSigned} className="flex-1 bg-green-500 hover:bg-green-400 text-black font-bold" data-testid="onboarding-finish">
                {allSigned ? (isLast ? 'Abschliessen ✓' : 'Weiter') : 'Bitte alles unterschreiben'}
              </Button>
            </div>
          </div>
        )}

        {/* STEP: Eigener Schritt (Baukasten) */}
        {current.type === 'custom' && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4" data-testid={`onboarding-custom-step-${current.id}`}>
            {current.description && <p className="text-white/60 text-sm">{current.description}</p>}
            {(current.fields || []).length === 0 && <p className="text-white/40 text-sm">Dieser Schritt enthält keine Felder.</p>}
            {(current.fields || []).filter(isVisible).map((f) => (
              <div key={f.id}>
                {f.type !== 'heading' && f.type !== 'info' && <Label className="text-white/80">{f.label}{f.required ? ' *' : ''}</Label>}
                {renderCustomField(f)}
              </div>
            ))}
            <div className="flex gap-2">
              {activeStepIdx > 0 && <Button variant="outline" onClick={goPrev} className="border-white/20 text-white bg-transparent hover:bg-white/10">Zurück</Button>}
              <Button onClick={() => saveCustom(current)} disabled={saving} className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-bold" data-testid="onboarding-custom-next">
                {saving ? 'Speichert…' : (isLast ? 'Abschliessen' : 'Weiter')} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
